'use client';

import { forwardRef } from 'react';

type BaseElement = 'button' | 'a' | 'div';

/**
 * Which zones the themed background spans.
 * - `'all'`          → style applied to the card element itself (default behavior)
 * - `'body+footer'`  → one continuous BG behind body + footer only (hero stays clean)
 * - `'body'`         → BG behind body only
 */
type BgSpan = 'all' | 'body+footer' | 'body';

interface BaseCardBaseProps {
  /** Grid layout variant */
  variant?: 'standard' | 'full-bleed';
  /** Render as button, anchor, or div */
  as?: BaseElement;
  /** Card header slot (hero image area for standard, content area for full-bleed) */
  header?: React.ReactNode;
  /** Card body slot (text content area) */
  body?: React.ReactNode;
  /** Card footer slot (CTA area) */
  footer?: React.ReactNode;
  className?: string;
  /** Style applied to the card element itself */
  style?: React.CSSProperties;
  /**
   * Where to render `bgStyle`. Defaults to 'all' (applied to the card element via `style`).
   * Use 'body+footer' for a single continuous BG image across body and footer zones.
   */
  bgSpan?: BgSpan;
  /** Background style for the scoped backdrop (only used when bgSpan !== 'all') */
  bgStyle?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  /** Background element rendered behind all content at z-0 (e.g. a video + overlay).
   *  Absolutely positioned to cover the entire card. */
  bgElement?: React.ReactNode;
  /** Overlay elements rendered on top of the card (badges, dot indicators).
   *  These are absolutely positioned and don't affect grid layout. */
  children?: React.ReactNode;
}

export type BaseCardProps = BaseCardBaseProps;

/**
 * Grid-based card wrapper with two layout variants:
 * - `standard`: grid-rows [auto 1fr auto] — hero, flexible body, CTA
 * - `full-bleed`: grid-rows [1fr auto] — full content area + CTA
 *
 * Background spanning:
 * - `style` on the card + `bgSpan='all'` (default) → BG on entire card
 * - `bgStyle` + `bgSpan='body+footer'` → one continuous BG behind body+footer
 * - `bgStyle` + `bgSpan='body'` → BG behind body only
 *
 * The backdrop is a single div placed behind the content slots using explicit
 * grid-row placement, so background images render as one continuous image.
 */
export const BaseCard = forwardRef<HTMLElement, BaseCardProps>(function BaseCard(
  {
    variant = 'standard',
    as = 'button',
    header,
    body,
    footer,
    className = '',
    style,
    bgSpan = 'all',
    bgStyle,
    bgElement,
    onClick,
    href,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    children,
  },
  ref
) {
  // Both variants use subgrid so cards align across the same grid row.
  // Standard: row 1=header, row 2=body, row 3=footer
  // Full-bleed: rows 1+2=body (merged), row 3=footer
  const gridRows = 'row-span-3 grid-rows-[subgrid]';

  const sharedClassName = `relative grid grid-cols-1 h-full w-full overflow-hidden shadow-md transition-all duration-300 hover:scale-[1.005] hover:shadow-xl ${gridRows} ${className}`;

  const sharedProps = {
    className: sharedClassName,
    style,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
  };

  // Compute explicit grid-row for the scoped backdrop.
  // Standard: row 1=header, row 2=body, row 3=footer
  // Full-bleed: rows 1+2=body (merged), row 3=footer
  const hasBackdrop = bgSpan !== 'all' && !!bgStyle;
  const backdropGridRow = (() => {
    if (!hasBackdrop) return undefined;
    if (variant === 'standard') {
      if (bgSpan === 'body+footer') return '2 / -1';
      if (bgSpan === 'body') return '2 / 3';
    } else {
      // Full-bleed: body is rows 1-2, footer is row 3
      if (bgSpan === 'body+footer') return '1 / -1';
      if (bgSpan === 'body') return '1 / 3';
    }
    return undefined;
  })();

  // Row assignments for slots — explicit so they share cells with the backdrop
  // Standard: header=row1, body=row2, footer=row3
  // Full-bleed: body spans rows 1+2 (no header), footer=row3
  const headerRow = '1 / 2';
  const bodyRow = variant === 'standard' ? '2 / 3' : '1 / 3';
  const footerRow = '3 / 4';

  const content = (
    <>
      {/* Full-card background element (e.g. video + overlay) — z-0, behind all grid content */}
      {bgElement && <div className="absolute inset-0 z-0 overflow-hidden">{bgElement}</div>}

      {/* Overlays — absolute positioned, outside grid flow */}
      {children && <div className="pointer-events-none absolute inset-0 z-10">{children}</div>}

      {/* Scoped background — one continuous BG image spanning specified grid rows.
          Placed in column 1 with explicit row, so it overlaps body/footer without
          adding extra rows. z-0 keeps it behind content. */}
      {hasBackdrop && (
        <div style={{ gridRow: backdropGridRow, gridColumn: '1 / -1', ...bgStyle }} />
      )}

      {/* Grid slots — explicit row+column placement so they layer on top of the backdrop */}
      {variant === 'standard' && (
        <div className="z-[1] min-h-0 min-w-0" style={{ gridRow: headerRow, gridColumn: '1 / -1' }}>
          {header}
        </div>
      )}
      <div
        className="relative z-[1] min-h-0 min-w-0"
        style={{ gridRow: bodyRow, gridColumn: '1 / -1' }}
      >
        {body}
      </div>
      <div
        className="relative z-[1] min-h-0 min-w-0"
        style={{ gridRow: footerRow, gridColumn: '1 / -1' }}
      >
        {footer}
      </div>
    </>
  );

  if (as === 'a') {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...sharedProps}
      >
        {content}
      </a>
    );
  }

  if (as === 'div') {
    return (
      <div ref={ref as React.Ref<HTMLDivElement>} {...sharedProps} role="button" tabIndex={0}>
        {content}
      </div>
    );
  }

  return (
    <button ref={ref as React.Ref<HTMLButtonElement>} type="button" {...sharedProps}>
      {content}
    </button>
  );
});
